import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useCallback, useEffect, useRef } from "react";
import { type UseFormReturn } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";

import { type AuthMode } from "../libs";
import { AuthMethod, type LoginSignUpFormFields } from "../types";

interface UseAutoLoginFromUrlProps {
  phoneNumberFromUrl?: string;
  authMethod: AuthMethod;
  authMode: AuthMode;
  authModeIsLogin: boolean;
  formState: { isSubmitting: boolean };
  formMethods: UseFormReturn<LoginSignUpFormFields>;
  setAuthMethod: (method: AuthMethod) => void;
  setLegacyIsSignUp: (mode: AuthMode) => void;
  initiateLogin: (values: { phoneNumber: string; email: string }) => Promise<void>;
}

export function useAutoLoginFromUrl(props: UseAutoLoginFromUrlProps) {
  const {
    phoneNumberFromUrl,
    authMethod,
    authMode,
    authModeIsLogin,
    formState,
    formMethods,
    setAuthMethod,
    setLegacyIsSignUp,
    initiateLogin,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const hasTriggeredAutoLogin = useRef(false);

  const clearPhoneNumberFromUrl = useCallback(() => {
    if (!phoneNumberFromUrl) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("phoneNumber");

    const newSearch = searchParams.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

    // Replace the current URL without adding to history
    history.replace(newUrl);
  }, [phoneNumberFromUrl, location.search, location.pathname, history]);

  const triggerAutoLogin = useCallback(async () => {
    if (hasTriggeredAutoLogin.current) {
      return;
    }

    hasTriggeredAutoLogin.current = true;

    void formMethods.handleSubmit(async (formData: LoginSignUpFormFields) => {
      try {
        setLegacyIsSignUp(authMode);
        await initiateLogin({
          phoneNumber: formData.phoneNumber,
          email: "",
        });

        // Clear phone number from URL after successful auto-login
        clearPhoneNumberFromUrl();

        logEvent(APP_V2_USER_EVENTS.AUTO_LOGIN_FROM_URL_WITH_PHONE_NUMBER as string, {
          phoneNumber: formData.phoneNumber,
        });
      } catch (error) {
        logError(APP_V2_APP_EVENTS.AUTO_LOGIN_FROM_URL_FAILURE as string, {
          error,
        });
      }
    })();
  }, [formMethods, authMode, initiateLogin, setLegacyIsSignUp, clearPhoneNumberFromUrl]);

  useEffect(() => {
    if (!phoneNumberFromUrl || !authModeIsLogin) {
      return undefined;
    }

    // First, set auth method to phone if not already set
    if (authMethod !== AuthMethod.PHONE) {
      setAuthMethod(AuthMethod.PHONE);
      return undefined;
    }

    if (!formState.isSubmitting) {
      const timer = setTimeout(() => {
        void triggerAutoLogin();
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }

    return undefined;
  }, [
    phoneNumberFromUrl,
    authMethod,
    authModeIsLogin,
    formState.isSubmitting,
    setAuthMethod,
    triggerAutoLogin,
  ]);
}
